import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import axiosInstance from '../../config/axios.js';
import userService from '../../services/UserService.js';
import { Users, Calendar, ArrowLeft} from "lucide-react";

const EditUser = () => {
  const { userId } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    city: '',
    location: '',
    dob: '',
    organization_name: '',
    document_type: '',
    document_number: '',
    role_id: '',
    status: 'active',
    gst_no: '',
    tank_cleaning_rates: '',
    onhire_rates: '',
    ei_receipt_rates: '',
    dry_box_rates: '',
    ti_report_rates: '',
    cc_email: '',
    bcc_email: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [documentAttachment, setDocumentAttachment] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [existingProfilePhoto, setExistingProfilePhoto] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [hasExistingDocument, setHasExistingDocument] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const navigate = useNavigate();

  const documentTypes = [
    { value: 'Aadhar', label: 'AADHAR' },
    { value: 'PAN', label: 'PAN' },
    { value: 'Driving License', label: 'DRIVING LICENSE' },
    { value: 'Voter ID', label: 'VOTER ID' },
    { value: 'Passport', label: 'PASSPORT' }
  ];
  const userStatuses = ['active', 'inactive'];
  const isClient = !!(formData.role_id && roles.find(r => r.id === parseInt(formData.role_id))?.role_name === 'Client');

  useEffect(() => {
    fetchUserAndRoles();
  }, [userId]);

  const fetchUserAndRoles = async () => {
    try {
      const [userRes, rolesRes] = await Promise.all([
        userService.getById(userId),
        axiosInstance.get('/roles'),
      ]);
      
      const userData = userRes.data;
      // Convert email arrays to comma-separated strings for display
      const FORM_FIELDS = [
        'name', 'email', 'contact', 'city', 'location', 'dob',
        'organization_name', 'document_type', 'document_number',
        'role_id', 'status', 'gst_no', 'tank_cleaning_rates',
        'onhire_rates', 'ei_receipt_rates', 'dry_box_rates',
        'ti_report_rates', 'cc_email', 'bcc_email'
      ];
      const formattedUserData = {};
      FORM_FIELDS.forEach(key => {
        if (key === 'cc_email' || key === 'bcc_email') {
          formattedUserData[key] = Array.isArray(userData[key]) ? userData[key].join(', ') : (userData[key] || '');
        } else {
          formattedUserData[key] = userData[key] ?? '';
        }
      });
      setFormData(formattedUserData);
      setRoles(rolesRes.data.data);
      
      // Set existing profile photo
      if (userData.profile_photo_base64) {
        setExistingProfilePhoto(`data:image/jpeg;base64,${userData.profile_photo_base64}`);
      } else {
        setExistingProfilePhoto(userService.getProfilePhoto(userId));
      }
      
      // Set existing document URL
      setExistingDocument(userService.getDocumentAttachment(userId));
      
      // Check if document exists by verifying the document endpoint
      checkDocumentExists(userId);
      
    } catch (err) {
      Alert.error('Failed to load user data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkDocumentExists = async (userId) => {
    try {
      const documentUrl = userService.getDocumentAttachment(userId);
      const response = await fetch(documentUrl, { method: 'HEAD' });
      
      if (response.ok) {
        setHasExistingDocument(true);
      } else {
        setHasExistingDocument(false);
      }
    } catch (error) {
      // If fetch fails, document doesn't exist
      setHasExistingDocument(false);
      console.log('No existing document found');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleEmailListChange = (e) => {
  //   const { name, value } = e.target;
  //   // Convert comma-separated string to array, trim whitespace
  //   const emailArray = value.split(',').map(email => email.trim()).filter(email => email);
  //   setFormData((prev) => ({ ...prev, [name]: emailArray }));
  // };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        Alert.error('Profile photo must be less than 1MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Document must be less than 2MB');
        return;
      }
      setDocumentAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    const fileInput = document.getElementById('profile-photo-input');
    if (fileInput) fileInput.value = '';
  };

  const removeExistingProfilePhoto = () => {
    setExistingProfilePhoto(null);
  };

  const removeDocumentAttachment = () => {
    setDocumentAttachment(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('document-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert comma-separated email strings to arrays
      const submissionData = {
        ...formData,
        cc_email: formData.cc_email ? formData.cc_email.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc_email: formData.bcc_email ? formData.bcc_email.split(',').map(e => e.trim()).filter(Boolean) : []
      };
      // Safety net: never send binary or read-only fields
      delete submissionData.profile_photo_base64;
      delete submissionData.document_attachment_base64;
      
      await userService.update(userId, submissionData, profilePhoto, documentAttachment);
      Alert.success('User updated successfully!');
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading user data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const displayPhoto = profilePhotoPreview || existingProfilePhoto;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Users className="w-5 h-5 text-buttonBg" /> 
                      Edit User
                    </h1>
                  </div>
                  <button
                          onClick={() => navigate('/users')}
                          className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <ArrowLeft
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Back to Users</span>
                        </button>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Profile Photo Section */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Profile Photo</h3>
                    <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-gray-300 
                                      overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {displayPhoto ? (
                          <img 
                            src={displayPhoto} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
                                  <rect width="128" height="128" fill="#e5e7eb"/>
                                  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="48" font-weight="bold">
                                    ${formData.name ? formData.name.charAt(0).toUpperCase() : 'U'}
                                  </text>
                                </svg>
                              `)}`;
                            }}
                          />
                        ) : (
                          <span className="text-3xl sm:text-4xl text-gray-400">👤</span>
                        )}
                      </div>
                      <div className="flex-1 w-full">
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          {existingProfilePhoto && !profilePhotoPreview ? 'Update Profile Photo' : 'Upload Profile Photo'}
                        </label>
                        <input
                          id="profile-photo-input"
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 1MB)</p>
                        {profilePhotoPreview && (
                          <button
                            type="button"
                            onClick={removeProfilePhoto}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove New Photo
                          </button>
                        )}
                        {existingProfilePhoto && !profilePhotoPreview && (
                          <button
                            type="button"
                            onClick={removeExistingProfilePhoto}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Existing Photo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Full Name <span className="text-danger font-bold">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Organization Name 
                          {formData.role_id && roles.find(r => r.id === parseInt(formData.role_id))?.role_name === 'Client' && (
                            <span className="text-danger font-bold pl-1">*</span>
                          )}
                        </label>
                        <input
                          type="text"
                          name="organization_name"
                          value={formData.organization_name || ''}
                          onChange={handleChange}
                          placeholder="Enter organization name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required={formData.role_id && roles.find(r => r.id === parseInt(formData.role_id))?.role_name === 'Client'}
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email <span className="text-danger font-bold">*</span></label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Date of Birth</label>
                        <div className="relative">
                          <input
                            type="date"
                            name="dob"
                            value={formData.dob || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Contact Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Contact Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number <span className="text-danger font-bold">*</span></label>
                        <input
                          type="text"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">City <span className="text-danger font-bold">*</span></label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Location <span className="text-danger font-bold">*</span></label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Document Information */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Document Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Document Type <span className="text-danger font-bold">*</span></label>
                          <select
                            name="document_type"
                            value={formData.document_type}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          >
                            {documentTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Document Number <span className="text-danger font-bold">*</span></label>
                          <input
                            type="text"
                            name="document_number"
                            value={formData.document_number}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div className="sm:col-span-2 mt-4">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Document Attachment</label>
                            
                            {/* View Existing Document Button */}
                            {hasExistingDocument && !documentPreview && (
                              <button
                                type="button"
                                onClick={openDocumentModal}
                                className="mb-3 px-4 py-2 bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Existing Document
                              </button>
                            )}
                            
                            <input
                              id="document-attachment-input"
                              type="file"
                              accept="image/*"
                              onChange={handleDocumentAttachmentChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            />
                            <p className="text-xs text-gray-600 mt-1">
                              {hasExistingDocument ? 'Upload new document to replace existing one (Max 2MB)' : 'Upload scanned copy (Max 2MB)'}
                            </p>
                            
                            {documentPreview && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">New Document Preview:</p>
                                <div className="relative inline-block">
                                  <img 
                                    src={documentPreview} 
                                    alt="Document Preview" 
                                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                                  />
                                  <button
                                    type="button"
                                    onClick={removeDocumentAttachment}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                    title="Remove document"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                    </div>
                    {/* Section 4: Role and Status */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Role and Status</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Role <span className="text-danger font-bold">*</span></label>
                          <select
                            name="role_id"
                            value={formData.role_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          >
                            <option value="">Select Role</option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.role_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Status</label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          >
                            {userStatuses.map((status) => (
                              <option key={status} value={status}>
                                {status.toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Section 5: Client Rates — only shown when Client role is selected */}
                  {isClient && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Client Rates</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">GST No</label>
                          <input
                            type="text"
                            name="gst_no"
                            value={formData.gst_no || ''}
                            onChange={handleChange}
                            placeholder="Enter GST number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank Cleaning Rates</label>
                          <input
                            type="number"
                            name="tank_cleaning_rates"
                            value={formData.tank_cleaning_rates || ''}
                            onChange={handleChange}
                            placeholder="Enter rate"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Onhire Rates</label>
                          <input
                            type="number"
                            name="onhire_rates"
                            value={formData.onhire_rates || ''}
                            onChange={handleChange}
                            placeholder="Enter rate"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">EI Receipt Rates</label>
                          <input
                            type="number"
                            name="ei_receipt_rates"
                            value={formData.ei_receipt_rates || ''}
                            onChange={handleChange}
                            placeholder="Enter rate"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Dry Box Rates</label>
                          <input
                            type="number"
                            name="dry_box_rates"
                            value={formData.dry_box_rates || ''}
                            onChange={handleChange}
                            placeholder="Enter rate"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">TI Report Rates</label>
                          <input
                            type="number"
                            name="ti_report_rates"
                            value={formData.ti_report_rates || ''}
                            onChange={handleChange}
                            placeholder="Enter rate"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            CC Email(s) <span className="text-gray-500 text-xs">(comma-separated)</span>
                          </label>
                          <input
                            type="text"
                            name="cc_email"
                            value={formData.cc_email || ''}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="email1@example.com, email2@example.com"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            BCC Email(s) <span className="text-gray-500 text-xs">(comma-separated)</span>
                          </label>
                          <input
                            type="text"
                            name="bcc_email"
                            value={formData.bcc_email || ''}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="email1@example.com, email2@example.com"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg"
                    >
                      {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />
                    <span className="relative z-10">{submitting ? 'Updating...' : 'Update User'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/users')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					            {/* ICON 
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      {hasExistingDocument && (
        <DocumentModal
          isOpen={isDocModalOpen}
          onClose={closeDocumentModal}
          documentUrl={existingDocument}
          documentType={formData.document_type}
        />
      )}
    </div>
  );
};

export default EditUser;